import {
    BoxProps,
    forwardRef,
    SimpleGrid,
    useRadioGroup,
    VStack,
} from '@chakra-ui/react';
import RadioInput, { RadioInputProps } from './RadioInput';
import { disabledStyleProps } from './styleHelpers';

export type RadioInputOption = {
    value: string | number | undefined;
    label: React.ReactNode;
    header?: JSX.Element;
    isDisabled?: boolean;
};

export interface RadioInputGroupProps extends Omit<BoxProps, 'onChange'> {
    name: string;
    value: string;
    onChange: (value: string) => void;
    direction?: 'row' | 'column';
    options: RadioInputOption[];
    inputProps?: RadioInputProps;
}

const RadioInputGroup = forwardRef<RadioInputGroupProps, 'input'>(
    (
        {
            name,
            options,
            value,
            onChange,
            color,
            inputProps = {},
            autoFocus,
            direction = 'column',
            ...rest
        },
        ref
    ) => {
        const { getRootProps, getRadioProps } = useRadioGroup({
            name,
            value,
            onChange,
        });

        const group = getRootProps();
        const Parent = direction === 'column' ? VStack : SimpleGrid;
        const columns = direction === 'column' ? undefined : options.length;

        return (
            <Parent {...rest} {...group} alignItems="stretch" columns={columns}>
                {options.map(
                    ({ value, label, header, isDisabled = false }, i) => {
                        const radio = getRadioProps({ value });
                        const props = {
                            autoFocus,
                            color,
                            header,
                            ...radio,
                            ...inputProps,
                        };
                        if (i === 0) props['ref'] = ref;
                        return (
                            <RadioInput
                                key={value}
                                {...props}
                                isDisabled={isDisabled}
                                {...disabledStyleProps(isDisabled)}
                            >
                                {label}
                            </RadioInput>
                        );
                    }
                )}
            </Parent>
        );
    }
);

export default RadioInputGroup;
